import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Medication, MedicationEvent } from '@shared/types.ts';
import { api } from './client.ts';

// ─── Catalogue ──────────────────────────────────────────────

export function useMedications() {
  return useQuery({
    queryKey: ['medications'],
    queryFn: () => api<{ medications: Medication[] }>('/api/medications'),
    select: (d) => d.medications,
  });
}

function useInvalidateMeds() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['medications'] });
    qc.invalidateQueries({ queryKey: ['medication_events'] });
  };
}

export function useCreateMedication() {
  const inv = useInvalidateMeds();
  return useMutation({
    mutationFn: (vars: {
      name: string;
      unit: string;
      default_dose?: number | null;
      min_interval_minutes?: number | null;
      color?: string | null;
      notes?: string | null;
    }) =>
      api<{ medication: Medication }>('/api/medications', { method: 'POST', json: vars }),
    onSuccess: () => inv(),
  });
}

export function useUpdateMedication() {
  const inv = useInvalidateMeds();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api(`/api/medications/${id}`, { method: 'PATCH', json: patch }),
    onSuccess: () => inv(),
  });
}

export function useDeleteMedication() {
  const inv = useInvalidateMeds();
  return useMutation({
    mutationFn: (id: string) => api(`/api/medications/${id}`, { method: 'DELETE' }),
    onSuccess: () => inv(),
  });
}

// ─── Dose events ────────────────────────────────────────────

export function useMedicationEvents(babyId: string | null | undefined) {
  return useQuery({
    queryKey: ['medication_events', { babyId }],
    enabled: !!babyId,
    queryFn: () =>
      api<{ events: MedicationEvent[] }>(`/api/medications/events?baby=${babyId}`),
    select: (d) => d.events,
  });
}

export function useCreateMedicationEvent() {
  const inv = useInvalidateMeds();
  return useMutation({
    mutationFn: (vars: {
      medication_id: string;
      dose: number;
      unit: string;
      occurred_at?: number;
      notes?: string | null;
    }) =>
      api<{ event: MedicationEvent }>('/api/medications/events', {
        method: 'POST',
        json: vars,
      }),
    onSuccess: () => inv(),
  });
}

export function useUpdateMedicationEvent() {
  const inv = useInvalidateMeds();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api(`/api/medications/events/${id}`, { method: 'PATCH', json: patch }),
    onSuccess: () => inv(),
  });
}

export function useDeleteMedicationEvent() {
  const inv = useInvalidateMeds();
  return useMutation({
    mutationFn: (id: string) => api(`/api/medications/events/${id}`, { method: 'DELETE' }),
    onSuccess: () => inv(),
  });
}
